import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ILoginRequest } from '../pages/login/login.interface';



export type Account = {
  id: number;
  cnpj: string;
  cpf: string;
  name: string;
  email: string;
  mobile: string;
  rdOrganizationId: string | null;
};

export type AccountSearchResult = Pick<Account, 'id' | 'name' | 'email' | 'cnpj' | 'cpf'>;

export type AccountSearchFilter = {
  name?: string;
  email?: string;
  document?: string;
};

type User = {
  id: number;
  accountId: number;
  function: string;
  cpf: string;
  name: string;
  email: string;
  mobile: string;
  birthdate: string | null;
  communicareUserId: string | null;
  administrativeGroup: {
    id: number;
    isSuperAdmin: boolean;
    role: 'FINANCEIRO' | 'OPERACIONAL' | null;
    createdAt: Date;
    userId: number;
  };
};

type AccountProfile = {
  account: Account;
  user: User;
};

export type Address = {
  zipCode: string;
  address: string;
  addressNumber: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  accountId: number;
  patientId: number;
};

type AccountAddress = Account & {
  address: Address;
};

@Injectable({
  providedIn: 'root',
})
export class AccountV2Service {
  url = environment.api_v2;

  constructor(private readonly http: HttpClient) {}

  public getProfile(): Observable<AccountProfile> {
    return this.http.get<AccountProfile>(`${this.url}/v2/account-info`);
  }

  public update(
    account: Omit<Account, 'id' | 'rdOrganizationId'>,
    address: Omit<Address, 'patientId' | 'accountId'>
  ) {
    return this.http.put(`${this.url}/v2/account`, { ...account, address }).pipe(take(1));
  }

  public find() {
    return this.http.get<AccountAddress>(`${this.url}/v2/account`);
  }

  public cnpjAlredyExists(cnpj: string) {
    return this.http
      .get<boolean>(`${this.url}/v2/account-info/validation/cnpj/${cnpj}`)
      .pipe(take(1));
  }

  public cpfAlredyExists(cpf: string) {
    return this.http
      .get<boolean>(`${this.url}/v2/account-info/validation/cpf/${cpf}`)
      .pipe(take(1));
  }

  public emailAlredyExists(email: string) {
    return this.http
      .get<boolean>(`${this.url}/v2/account-info/validation/email/${email}`)
      .pipe(take(1));
  }

  public searchAccounts(
    filter: AccountSearchFilter,
    page = 1,
    perPage = 20
  ): Observable<AccountSearchResult[]> {
    const params = new HttpParams()
      .set('filter', JSON.stringify(filter))
      .set('page', page)
      .set('perPage', perPage);

    return this.http
      .get<{ rows: AccountSearchResult[]; total: number }>(
        `${this.url}/v2/administrative/accounts/search`,
        { params }
      )
      .pipe(
        take(1),
        map((res) => res.rows)
      );
  }
}
